import React, { useRef } from 'react';
import { Box, TextField, Typography, Divider } from '@mui/material';
import {
  FormProps,
  FormHeader,
  DocumentUploadArea,
  AudioRecordingSection,
  WritingStyleSelector,
  InfoTip,
} from './shared';

const AcordoAlimentosGuardaForm: React.FC<FormProps> = ({
  documentType,
  values,
  onChange,
  uploadedFiles,
  onFileUpload,
  onFileRemove,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedStyle, setSelectedStyle] = React.useState(values.estilo_escrita || 'padrao');

  const handleFieldChange = (fieldId: string, value: any) => {
    onChange({
      ...values,
      [fieldId]: value,
    });
  };

  const handleRecordingComplete = (audioBlob: Blob) => {
    const audioFile = new File([audioBlob], `audio_${Date.now()}.webm`, {
      type: 'audio/webm',
    });
    onFileUpload([audioFile]);
  };

  return (
    <Box>
      <FormHeader 
        documentType={documentType}
        customTitle="Acordo de Alimentos, Guarda e Visitas"
        customDescription="Elaboração de acordo consensual envolvendo pensão alimentícia, guarda e regime de visitas"
      />

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        {/* 1. Nome da Parte Interessada */}
        <Box>
          <Typography variant="subtitle1" fontWeight={600} gutterBottom>
            Escreva o nome da parte interessada: <span style={{ color: '#d32f2f' }}>*</span>
          </Typography>
          <TextField
            fullWidth
            placeholder="Ex: Maria Souza."
            value={values.nome_cliente || ''}
            onChange={(e) => handleFieldChange('nome_cliente', e.target.value)}
            helperText="Separe os nomes com ';' caso você represente mais de uma parte"
            required
          />
        </Box>

        {/* 2. Descrição do Caso */}
        <Box>
          <Typography variant="subtitle1" fontWeight={600} gutterBottom>
            Conte algo que deva ser considerado no documento: <span style={{ color: '#d32f2f' }}>*</span>
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Escreva como falaria para um colega, sem usar comandos de IA
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={6}
            placeholder="Exemplo: Estou representando a Maria Souza nesse caso de regulamentação de guarda e alimentos. Ela é mãe de dois filhos menores com o João Silva. Eles querem definir guarda compartilhada, com residência fixa na casa da mãe, visitas quinzenais do pai e pensão de 30% do salário do genitor."
            value={values.fatos_caso || ''}
            onChange={(e) => handleFieldChange('fatos_caso', e.target.value)}
            inputProps={{ maxLength: 6000 }}
            required
          />
          <Typography
            variant="caption"
            color={(values.fatos_caso || '').length > 0 ? 'success.main' : 'text.secondary'}
            sx={{ mt: 0.5, display: 'block' }}
          >
            {(values.fatos_caso || '').length} de 6000 caracteres permitidos.
          </Typography>
        </Box>

        {/* 3. Nome da Outra Parte */}
        <Box>
          <Typography variant="subtitle1" fontWeight={600} gutterBottom>
            Escreva o nome da(s) outra(s) parte(s), se houver: <Typography component="span" variant="body2" color="text.secondary">(Opcional)</Typography>
          </Typography>
          <TextField
            fullWidth
            placeholder="Ex: A outra parte é João Silva."
            value={values.parte_contraria || ''}
            onChange={(e) => handleFieldChange('parte_contraria', e.target.value)}
          />
        </Box>

        {/* 4. Upload de Documentos (Opcional) */}
        <DocumentUploadArea
          uploadedFiles={uploadedFiles}
          onFileUpload={onFileUpload}
          onFileRemove={onFileRemove}
          suggestedDocs="Certidão de nascimento dos filhos, Comprovantes de renda, Comprovantes de despesas e Documentos de identificação."
          fileInputRef={fileInputRef as React.RefObject<HTMLInputElement>}
        />

        {/* Gravação de Áudio */}
        <AudioRecordingSection
          onRecordingComplete={handleRecordingComplete}
          description="Grave um áudio descrevendo os detalhes do acordo que a IA irá transcrever automaticamente."
        />
      </Box>

      <Divider sx={{ my: 3 }} />

      {/* Estilo de Escrita */}
      <WritingStyleSelector
        selectedStyle={selectedStyle}
        onStyleChange={(styleId) => {
          setSelectedStyle(styleId);
          handleFieldChange('estilo_escrita', styleId);
        }}
      />

      <InfoTip message="O acordo deve contemplar: valor e forma de pagamento da pensão alimentícia, tipo de guarda (unilateral ou compartilhada), residência fixa do menor, regime de visitas (dias, horários, feriados, férias) e responsabilidades de cada genitor. Pode ser feito por escritura pública ou judicialmente." />
    </Box>
  );
};

export default AcordoAlimentosGuardaForm;

